import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { User } from '@/types';
import { setUserContext, clearUserContext } from '@/lib/sentry';
import { analyticsService } from '@/services/analyticsService';

/**
 * Sanitiza payload de profile para o banco de dados
 * - Converte strings vazias em null
 * - Valida formato de data_nascimento (YYYY-MM-DD)
 * - Remove campos undefined
 */
function sanitizeProfileForDB(payload: Partial<User>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(payload)) {
    // Remover undefined
    if (value === undefined) continue;
    
    // Tratar data_nascimento especialmente
    if (key === 'data_nascimento') {
      if (!value || value === '' || (typeof value === 'string' && value.trim() === '')) {
        sanitized[key] = null;
        continue;
      }
      
      // Validar formato YYYY-MM-DD
      if (typeof value === 'string') {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) {
          console.error('❌ Formato de data inválido:', value);
          throw new Error('Data de nascimento deve estar no formato YYYY-MM-DD');
        }
        sanitized[key] = value;
      } else {
        sanitized[key] = null;
      }
      continue;
    }
    
    // Para outros campos string: converter "" em null
    if (typeof value === 'string') {
      sanitized[key] = value.trim() === '' ? null : value.trim();
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (nome: string, email: string, password: string, language?: 'pt-BR' | 'en' | 'es') => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<{ success: boolean }>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    try {
      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Fetch user profile (public data)
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        // Fetch private data (only owner can access)
        const { data: privateData } = await supabase
          .from('profile_private')
          .select('*')
          .eq('user_id', session.user.id)
          .single();

        if (profile) {
          // Merge public and private data
          const fullProfile = {
            ...profile,
            ...(privateData || {}),
          };

          // Sincronizar idioma com i18next
          if (profile.language) {
            const i18n = await import('@/i18n/config');
            await i18n.default.changeLanguage(profile.language);
          }
          
          // ✅ FASE 2: Configurar contexto do usuário no Sentry
          setUserContext(profile.id, session.user.email, profile.nome);
          
          set({
            user: fullProfile as User,
            session,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } else {
        set({ isLoading: false });
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Fetch user profile (public data)
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          // Fetch private data (only owner can access)
          const { data: privateData } = await supabase
            .from('profile_private')
            .select('*')
            .eq('user_id', session.user.id)
            .single();

          if (profile) {
            // Merge public and private data
            const fullProfile = {
              ...profile,
              ...(privateData || {}),
            };

            // ✅ FASE 2: Configurar contexto do usuário no Sentry
            setUserContext(profile.id, session.user.email, profile.nome);
            
            // ✅ FASE 2: Track login event
            analyticsService.trackEngagement('session_start');
            
            set({
              user: fullProfile as User,
              session,
              isAuthenticated: true,
            });
          }
        } else if (event === 'SIGNED_OUT') {
          // ✅ FASE 2: Limpar contexto do Sentry
          clearUserContext();
          
          set({
            user: null,
            session: null,
            isAuthenticated: false,
          });
        }
      });
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({ isLoading: false });
    }
  },

  login: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Fetch public profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        // Fetch private data
        const { data: privateData } = await supabase
          .from('profile_private')
          .select('*')
          .eq('user_id', data.user.id)
          .single();

        if (profile) {
          // Merge public and private data
          const fullProfile = {
            ...profile,
            ...(privateData || {}),
          };

          // ✅ FASE 2: Configurar contexto do usuário no Sentry + Analytics
          setUserContext(profile.id, data.user.email, profile.nome);
          analyticsService.trackEngagement('session_start');
          
          set({
            user: fullProfile as User,
            session: data.session,
            isAuthenticated: true,
          });
          
          // 🚀 Iniciar pré-carregamento de dados em background
          setTimeout(() => {
            import('@/hooks/useDataPrefetch').then(({ useDataPrefetch }) => {
              console.log('🚀 Iniciando prefetch após login...');
            });
          }, 100);
          
          return { success: true };
        }
      }

      return { success: false, error: 'Perfil não encontrado' };
    } catch (error) {
      return { success: false, error: 'Erro ao fazer login' };
    }
  },

  signup: async (nome: string, email: string, password: string, language: 'pt-BR' | 'en' | 'es' = 'pt-BR') => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            nome,
            language,
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Wait a bit for the trigger to create the profile
        await new Promise(resolve => setTimeout(resolve, 1000));

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profile) {
          set({
            user: profile as User,
            session: data.session,
            isAuthenticated: true,
          });
          return { success: true };
        }
      }

      return { success: false, error: 'Erro ao criar perfil' };
    } catch (error) {
      return { success: false, error: 'Erro ao criar conta' };
    }
  },

  logout: async () => {
    console.log('🔐 Iniciando logout rápido...');
    
    try {
      // ✅ FASE 2: Track logout + Limpar Sentry context
      const currentUser = get().user;
      if (currentUser) {
        analyticsService.trackEngagement('session_end');
      }
      clearUserContext();
      
      // 1. LOGOUT DO SUPABASE PRIMEIRO (operação crítica)
      await supabase.auth.signOut();
      console.log('✅ Logout do Supabase concluído');
      
      // 2. Resetar estado de autenticação IMEDIATAMENTE
      set({
        user: null,
        session: null,
        isAuthenticated: false,
      });
      
      // 3. Cleanup não-bloqueante (fire-and-forget)
      setTimeout(() => {
        try {
          console.log('🧹 Iniciando cleanup assíncrono...');
          
          // Limpar localStorage
          const keysToRemove = [
            'lifeday-app',
            'lifeday-onboarding',
            'lifeday-draft-goal',
            'lastWeeklyScoreSync',
          ];
          
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            console.log(`  ✅ Removido: ${key}`);
          });
          
          // Limpar store (será resetado no próximo login de qualquer forma)
          import('@/stores/useAppStore').then(({ useAppStore }) => {
            const store = useAppStore.getState();
            store.setGoals([]);
            store.setHabits([]);
            store.setChallenges([]);
            console.log('✅ Store limpo');
          });
          
          console.log('✅ Cleanup completo');
        } catch (error) {
          console.warn('⚠️ Erro no cleanup (não crítico):', error);
        }
      }, 0);
      
      console.log('🔐 Logout concluído com sucesso');
    } catch (error) {
      console.error('❌ Erro ao fazer logout:', error);
      // Resetar estado mesmo se o logout falhar
      set({
        user: null,
        session: null,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  updateUser: async (userData: Partial<User>) => {
    const currentUser = get().user;
    if (!currentUser) {
      console.error('🔴 Usuário não encontrado no estado');
      throw new Error('Usuário não encontrado');
    }

    console.log('📨 Payload recebida em updateUser:', userData);
    
    // 🧹 SANITIZAR PAYLOAD - Converter strings vazias em null e validar data
    let sanitizedData: Record<string, any>;
    try {
      sanitizedData = sanitizeProfileForDB(userData);
      console.log('🧹 Payload sanitizada:', sanitizedData);
    } catch (error: any) {
      console.error('❌ Erro na sanitização:', error);
      throw new Error(error.message || 'Erro ao validar dados');
    }

    // Separate public and private fields
    const privateFields = ['telefone', 'data_nascimento', 'cidade_uf', 'linkedin', 'instagram', 'website', 'empresa', 'cargo'];
    const publicData: Record<string, any> = {};
    const privateData: Record<string, any> = {};

    for (const [key, value] of Object.entries(sanitizedData)) {
      if (privateFields.includes(key)) {
        privateData[key] = value;
      } else {
        publicData[key] = value;
      }
    }
    
    // ⚡ OPTIMISTIC UPDATE - Atualizar UI IMEDIATAMENTE com dados sanitizados
    const previousUser = { ...currentUser };
    set({ user: { ...currentUser, ...sanitizedData } });
    console.log('⚡ UI atualizada otimisticamente');

    try {
      console.time('⏱️ Database Update');
      
      // Update public profile data
      if (Object.keys(publicData).length > 0) {
        const { error: publicError } = await supabase
          .from('profiles')
          .update(publicData)
          .eq('id', currentUser.id);

        if (publicError) {
          console.error('🔴 Erro ao atualizar dados públicos:', publicError);
          set({ user: previousUser });
          throw publicError;
        }
      }

      // Update private profile data (upsert to handle new users)
      if (Object.keys(privateData).length > 0) {
        const { error: privateError } = await supabase
          .from('profile_private')
          .upsert({
            user_id: currentUser.id,
            ...privateData,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });

        if (privateError) {
          console.error('🔴 Erro ao atualizar dados privados:', privateError);
          set({ user: previousUser });
          throw privateError;
        }
      }

      console.timeEnd('⏱️ Database Update');
      console.log('✅ Perfil atualizado no banco com sucesso');
      
      return { success: true };
    } catch (error) {
      console.error('🔴 Erro ao atualizar usuário:', error);
      throw error;
    }
  },
}));
